<script setup lang="ts">
    import { computed } from 'vue';
    import { useBookingStore } from '~/stores/booking.js';
    import Divider from './common/Divider.vue';
    import BenefitsCards from './common/BenefitsCards.vue';
    import { CurrencySymbol } from '~/enums/CurrencySymbols.js';

    const bookingStore = useBookingStore();
    const currentCurrency = computed(() => bookingStore.state.currency);
    const standardPackages = computed(() => bookingStore.state.standard_packages);
    const formattedPrice = computed(() => {
        const price = standardPackages.value?.[0]?.price;
        if (!price) return '';
        return Number(price) % 1 !== 0 ? Number(price).toFixed(2) : String(price);
    });

    const benefits = [
        {
            title: 'A complete plan, built around you',
            description: 'Training, nutrition and lifestyle coaching tailored to your body and goals.',
            icon: 'handshake',
        },
        {
            title: 'An expert managing everything',
            description: 'One trainer takes ownership of your result from start to finish.',
            icon: 'restaurant',
        },
        {
            title: 'Accountability seven days a week',
            description: ' Coaching and support outside the gym keep you consistent and on track.',
            icon: 'trending-up',
        },
        {
            title: 'Results you can measure',
            description: '50+ metrics tracked so you can see clear progress without guesswork.',
            icon: 'aap-shortcut',
        },
    ];

</script>

<template>
    <Divider />

    <div class="mb-10 text-center">
        <h2 class="font-bold text-3xl leading-10 mb-1">
            4-Session Discovery Package –
            <span class="text-[#7C2629]">
                {{ CurrencySymbol[currentCurrency as keyof typeof CurrencySymbol] }}{{ formattedPrice }}. 
            </span>
            No onboarding fee.
        </h2>
        <p class="leading-6">
            Experience the U.P. Method for yourself and see what world-class personal training can help you achieve.
        </p>
    </div>

    <div class="flex flex-col gap-2 lg:flex-row lg:gap-3">
        <BenefitsCards
            v-for="item in benefits"
            :key="item.title"
            :title="item.title"
            :description="item.description"
        >
            <template #icon>
                <Icon :icon="item.icon" aria-hidden="true" class="benefit-card-icon" />
            </template>
        </BenefitsCards>
    </div>

    <Divider />
</template>

<style scoped>
    .benefit-card-icon {
        margin-bottom: 16px;
        width: 32px;
        height: 32px;
        aspect-ratio: 1/1;
    }
</style>
